import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MessageSquare, Users, ArrowRight, Loader2,
  Mail, Bell, Clock
} from 'lucide-react';
import { apiRequest } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';

export default function Connect() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    unreadPersonal: 0,
    unreadGroup: 0,
    recentChats: [],
    activeGroups: []
  });

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      // Fetch unread messages count and all groups
      const [chatData, allGroupsData] = await Promise.all([
        apiRequest('/chat/unread-count').catch(() => ({ count: 0 })),
        apiRequest('/groups').catch(() => [])
      ]);

      // All groups are visible to everyone
      const groups = Array.isArray(allGroupsData) ? allGroupsData : [];

      // Calculate unread group messages (from all groups user might participate in)
      let unreadGroup = groups.reduce((acc, g) => acc + (g.unread_count || 0), 0);

      setStats({
        unreadPersonal: chatData.count || 0,
        unreadGroup: unreadGroup,
        recentChats: chatData.recent || [],
        activeGroups: groups.slice(0, 3)
      });
    } catch (error) {
      console.error('Failed to load connect stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-[#DC2626]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="connect-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <MessageSquare className="h-7 w-7 text-[#DC2626]" />
          Connect
        </h1>
        <p className="text-slate-500">
          Stay connected with your team through personal and group messaging
        </p>
      </div>

      {/* Main Cards */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Personal Chat Card */}
        <Card 
          className="cursor-pointer hover:shadow-lg transition-shadow border-2 hover:border-blue-300"
          onClick={() => navigate('/chat')}
          data-testid="personal-chat-card"
        >
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div className="w-14 h-14 rounded-2xl bg-blue-100 flex items-center justify-center">
                <MessageSquare className="h-7 w-7 text-blue-600" />
              </div>
              {stats.unreadPersonal > 0 && (
                <Badge className="bg-red-500 text-white text-lg px-3 py-1">
                  {stats.unreadPersonal}
                </Badge>
              )}
            </div>
            <CardTitle className="text-xl mt-4">Personal Chat</CardTitle>
            <CardDescription>
              Direct messages with colleagues
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div className="text-sm text-slate-500">
                {stats.unreadPersonal > 0 ? (
                  <span className="text-blue-600 font-medium">
                    {stats.unreadPersonal} unread message{stats.unreadPersonal !== 1 ? 's' : ''}
                  </span>
                ) : (
                  <span>No unread messages</span>
                )}
              </div>
              <Button variant="ghost" size="sm" className="text-blue-600">
                Open <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Group Chat Card */}
        <Card 
          className="cursor-pointer hover:shadow-lg transition-shadow border-2 hover:border-purple-300"
          onClick={() => navigate('/groups')}
          data-testid="group-chat-card"
        >
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div className="w-14 h-14 rounded-2xl bg-purple-100 flex items-center justify-center">
                <Users className="h-7 w-7 text-purple-600" />
              </div>
              {stats.unreadGroup > 0 && (
                <Badge className="bg-red-500 text-white text-lg px-3 py-1">
                  {stats.unreadGroup}
                </Badge>
              )}
            </div>
            <CardTitle className="text-xl mt-4">Group Chat</CardTitle>
            <CardDescription>
              Team discussions and announcements
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div className="text-sm text-slate-500">
                {stats.unreadGroup > 0 ? (
                  <span className="text-purple-600 font-medium">
                    {stats.unreadGroup} unread in groups
                  </span>
                ) : (
                  <span>{stats.activeGroups.length} active group{stats.activeGroups.length !== 1 ? 's' : ''}</span>
                )}
              </div>
              <Button variant="ghost" size="sm" className="text-purple-600">
                Open <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        <Card>
          <CardContent className="py-4 text-center">
            <Mail className="h-6 w-6 mx-auto mb-2 text-blue-500" />
            <p className="text-2xl font-bold text-slate-900">{stats.unreadPersonal}</p>
            <p className="text-xs text-slate-500">Unread Messages</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="py-4 text-center">
            <Users className="h-6 w-6 mx-auto mb-2 text-purple-500" />
            <p className="text-2xl font-bold text-slate-900">{stats.activeGroups.length}</p>
            <p className="text-xs text-slate-500">Active Groups</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="py-4 text-center">
            <Bell className="h-6 w-6 mx-auto mb-2 text-amber-500" />
            <p className="text-2xl font-bold text-slate-900">{stats.unreadPersonal + stats.unreadGroup}</p>
            <p className="text-xs text-slate-500">Total Unread</p>
          </CardContent>
        </Card>
      </div>

      {/* Active Groups List */}
      {stats.activeGroups.length > 0 && (
        <Card>
          <CardHeader className="py-3">
            <CardTitle className="text-base">Your Groups</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="space-y-2">
              {stats.activeGroups.map(group => (
                <div 
                  key={group.id}
                  className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 cursor-pointer"
                  onClick={() => navigate(`/groups/${group.id}`)}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                      <Users className="h-5 w-5 text-purple-600" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">{group.name}</p>
                      <p className="text-xs text-slate-500">{group.member_count || 0} members</p>
                    </div>
                  </div>
                  {group.unread_count > 0 && (
                    <Badge className="bg-red-100 text-red-700">{group.unread_count} new</Badge>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
